class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();

      // Enforce bonus thresholds after any render
      try {
        enforceBonusThresholds();
      } catch (e) {
        console.warn('Bonus enforcement skipped', e);
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Handle inline bonus add-to-cart forms to update drawer without navigating
document.addEventListener('submit', function(e) {
  const form = e.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!form.classList.contains('adacted-bonus-offer__form')) return;
  e.preventDefault();

  const formData = new FormData(form);
  fetch(routes.cart_add_url, {
    method: 'POST',
    headers: { 'Accept': 'application/json' },
    body: formData
  })
    .then(() => fetch(`${routes.cart_url}?section_id=cart-drawer`))
    .then((response) => response.text())
    .then((responseText) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const drawer = document.querySelector('cart-drawer');
      const newInner = html.querySelector('#CartDrawer');
      if (drawer && newInner) {
        const current = drawer.querySelector('#CartDrawer');
        if (current) current.innerHTML = newInner.innerHTML;
        drawer.open();
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      try { enforceBonusThresholds(); } catch(_){}
    })
    .catch((err) => console.error('Bonus add failed', err));
});

// Remove bonus items if thresholds no longer met
function enforceBonusThresholds() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  const t1 = parseInt(drawer.dataset.tier1 || '0');
  const t2 = parseInt(drawer.dataset.tier2 || '0');
  const t3 = parseInt(drawer.dataset.tier3 || '0');
  if (!(t1 || t2 || t3)) return;

  fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((cart) => {
      const spent = cart.total_price; // in cents
      const allowed = new Set();
      if (t1 && spent >= t1) allowed.add(1);
      if (t2 && spent >= t2) allowed.add(2);
      if (t3 && spent >= t3) allowed.add(3);

      const removals = cart.items
        .map((it, idx) => ({ it, line: idx + 1 }))
        .filter(({ it }) => it.properties && it.properties._bonus)
        .filter(({ it }) => {
          const tier = parseInt(it.properties._bonus_tier || '0');
          return !allowed.has(tier);
        });

      if (!removals.length) return;
      // Remove each by line via change endpoint, then refresh
      return Promise.all(
        removals.map(({ line }) =>
          fetch(routes.cart_change_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line, quantity: 0 })
          })
        )
      )
        .then(() => fetch(`${routes.cart_url}?section_id=cart-drawer`))
        .then((res) => res.text())
        .then((htmlText) => {
          const html = new DOMParser().parseFromString(htmlText, 'text/html');
          const newInner = html.querySelector('#CartDrawer');
          const current = document.querySelector('cart-drawer #CartDrawer');
          if (newInner && current) current.innerHTML = newInner.innerHTML;
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
        })
        .catch((e) => console.error('Bonus removal failed', e));
    })
    .catch(() => {});
}
